import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { tokens } from '../../theme/tokens';

type Tone = 'favorable' | 'unfavorable' | 'neutral';

const toneColor: Record<Tone, string> = {
  favorable: tokens.color.success,
  unfavorable: tokens.color.danger,
  neutral: tokens.color.textSecondary,
};

export const StatCard: React.FC<{
  title: string;
  value: React.ReactNode;
  subtitle?: string;
  /** Optional thin accent rule on the left edge. */
  accent?: string;
  /** Colors the subtitle to signal favorable / unfavorable finance states. */
  tone?: Tone;
  icon?: React.ReactNode;
  onClick?: () => void;
  ariaLabel?: string;
  valueVariant?: 'h3' | 'h4' | 'h5' | 'h6' | 'subtitle1' | 'subtitle2' | 'body1' | 'body2';
  valueNoWrap?: boolean;
  valueSx?: any;
}> = ({ title, value, subtitle, accent, tone, icon, onClick, ariaLabel, valueVariant, valueNoWrap, valueSx }) => {
  return (
    <Card
      sx={{
        borderRadius: `${tokens.radius.lg}px`,
        borderColor: tokens.color.border,
        boxShadow: 'none',
        overflow: 'hidden',
        position: 'relative',
        height: '100%',
        transition: 'border-color 120ms ease, background-color 120ms ease',
        ...(onClick
          ? {
              cursor: 'pointer',
              '&:hover': { borderColor: tokens.color.borderStrong, backgroundColor: tokens.color.surfaceMuted },
            }
          : {}),
        '&::before': accent
          ? { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: accent }
          : undefined,
      }}
    >
      <ButtonBase
        onClick={onClick}
        disabled={!onClick}
        aria-label={ariaLabel || title}
        sx={{ width: '100%', textAlign: 'left', alignItems: 'stretch', display: 'block', '&.Mui-disabled': { opacity: 1 } }}
      >
        <CardContent sx={{ pl: accent ? 2.25 : 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography
              variant="overline"
              sx={{ color: tokens.color.textSecondary, fontWeight: 600, lineHeight: 1.6 }}
            >
              {title}
            </Typography>
            {icon ? <Box sx={{ color: tokens.color.textMuted, display: 'flex' }}>{icon}</Box> : null}
          </Box>
          <Typography
            variant={valueVariant || 'h5'}
            noWrap={!!valueNoWrap}
            sx={{ fontWeight: 700, mt: 0.25, letterSpacing: '-0.02em', ...(valueSx || {}) }}
          >
            {value}
          </Typography>
          {subtitle ? (
            <Typography
              variant="body2"
              sx={{ mt: 0.25, color: tone ? toneColor[tone] : tokens.color.textSecondary, fontWeight: tone ? 600 : 400 }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </CardContent>
      </ButtonBase>
    </Card>
  );
};
